import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import React from 'react';

import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {schemaFieldsetName} from 'schemas/schema-interface/util/schemaFieldsetName';
import {
  SchemaInterfaceIdSelectorQuery,
  SchemaInterfaceIdSelectorQueryVariables,
} from 'schemas/shared/__generated/SchemaInterfaceIdSelectorQuery';
import FormGroup from 'ui-library/bs/FormGroup';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Menu from 'ui-library/menus/Menu';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import {capwords} from 'util/NameUtil';

export const NO_SELECTION_OPTION = {
  value: null,
  name: '—',
};

export interface SchemaInterfaceIdSelectorProps {
  disabled?: boolean;
  selectedInterfaceId: string | null;
  onChangeSelectedInterfaceId: (option: string | null) => void;
}

export interface SchemaInterfaceOption {
  id: string;
  name: string;
}

export interface ConnectedProps {
  interfaces: Array<SchemaInterfaceOption>;
}

function SchemaInterfaceSelector({
  disabled,
  interfaces,
  selectedInterfaceId: selectedInterface,
  onChangeSelectedInterfaceId: onChangeSelectedInterface,
}: SchemaInterfaceIdSelectorProps & ConnectedProps): JSX.Element {
  const selectedInterfaceOption = interfaces.find((option) => option.id === selectedInterface);
  const interfaceOptions = interfaces.map((interfaceOption) => ({
    value: interfaceOption.id,
    name: interfaceOption.name,
  }));

  return (
    <FormGroup>
      <FieldLabel label={capwords(schemaFieldsetName())} />
      <SelectDropdown
        displayName={selectedInterfaceOption ? selectedInterfaceOption.name : NO_SELECTION_OPTION.name}
        disabled={disabled}
        menu={
          <SearchableMenu
            width={Menu.WIDTHS.FULL}
            onSelect={onChangeSelectedInterface}
            optionDatas={[NO_SELECTION_OPTION, ...interfaceOptions]}
            selectedValue={selectedInterface}
          />
        }
      />
    </FormGroup>
  );
}

// Query is named differently than variable to prevent conflict with TagSchemaOrSchemaSelector query.
export const SCHEMA_INTERFACE_SELECTOR_QUERY: DocumentNode = gql`
  query SchemaInterfaceIdSelectorQuery @skipAudit @engComponent(name: SCHEMAS) {
    schemaInterfaces {
      id
      name
    }
  }
`;

export default function QueryingSchemaInterfaceSelector(props: SchemaInterfaceIdSelectorProps): JSX.Element {
  const queryResponse = useBenchlingQuery<
    SchemaInterfaceIdSelectorQuery,
    SchemaInterfaceIdSelectorQueryVariables
  >(SCHEMA_INTERFACE_SELECTOR_QUERY);

  if (queryResponse.type === QueryResultType.SUCCESS) {
    return <SchemaInterfaceSelector {...props} interfaces={queryResponse.data.schemaInterfaces} />;
  }

  return <SchemaInterfaceSelector {...props} disabled={true} interfaces={[]} />;
}
